'use client';
import React from 'react';
import { motion } from 'framer-motion';

const COLORS = {
  ACCENT_GOLD: '#faaf40',
  DARK_BG: '#101010',
  LIGHT_TEXT: '#f0f0f0',
};

const steps = [
  { step: 1, title: 'Assessment', desc: 'Tell us about your goals, dietary preferences, and daily routine in a simple quiz.' },
  { step: 2, title: 'Customization', desc: 'Our AI and nutrition experts craft a meal plan tailored specifically to your unique metabolism.' },
  { step: 3, title: 'Guidance', desc: 'Receive daily meal guides, grocery lists, and track your progress in real-time.' },
];

export default function HowItWorksSection() {
  return (
    <section
      // Responsive vertical padding: py-16 on mobile, py-24 on desktop
      className="relative py-16 sm:py-24 overflow-hidden"
      style={{
        background: `linear-gradient(to bottom, #1a1a1a, ${COLORS.DARK_BG})`,
        color: COLORS.LIGHT_TEXT,
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16 sm:mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold"
          >
            How It <span style={{ color: COLORS.ACCENT_GOLD }}>Works</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-4 text-gray-400 text-sm sm:text-base max-w-2xl mx-auto"
          >
            Your journey to a better you, simplified in three steps.
          </motion.p>
        </div>

        {/* Steps Container */}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
          
          {/* Connector Line (Desktop Only) - Visualizes the flow */}
          <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-0.5 bg-[#333] -z-10">
             <div className="w-full h-full bg-gradient-to-r from-transparent via-[#faaf40] to-transparent opacity-30" />
          </div>

          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.2 }} // Staggered delay for cascade effect
              className="relative flex flex-col items-center text-center group"
            >
              {/* Step Number Circle */}
              <div 
                className="w-16 h-16 mb-6 rounded-full flex items-center justify-center text-2xl font-bold relative z-10 transition-transform duration-300 group-hover:scale-110 shadow-[0_0_20px_rgba(0,0,0,0.5)]"
                style={{ 
                  backgroundColor: '#1c1c1c', 
                  border: `2px solid ${COLORS.ACCENT_GOLD}`,
                  color: COLORS.ACCENT_GOLD,
                }}
              >
                {s.step}
                
                {/* Inner Glow on hover */}
                <div className="absolute inset-0 rounded-full bg-[#faaf40] opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
              </div>

              {/* Card Content */}
              <div className="bg-[#181818] border border-white/5 rounded-2xl p-6 sm:p-8 w-full h-full shadow-lg hover:border-[#faaf40]/30 hover:shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] transition-all duration-300">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 text-white group-hover:text-[#faaf40] transition-colors">
                  {s.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile Call to Action (Optional) */}
        <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-16 text-center"
        >
            <button className="text-sm font-bold uppercase tracking-widest border-b-2 border-[#faaf40] pb-1 hover:text-[#faaf40] transition-colors">
                Start Your Assessment &rarr;
            </button>
        </motion.div>

      </div>
    </section>
  );
}